import type {
  ScInstallInfo,
  ScMetricTile,
  ScSchemaItem,
  ScStatusKey,
  ScPriorityKey,
  ScToneKey,
  ScValidation,
} from '@elva/ui';
import { growthGet } from '@/data/http';
import { deepCamel, num, relativeTimeFromIso, str } from '@/data/adapt';
import { enumLower } from '@/data/adapt';

interface BeSchemaByUrl {
  url?: string;
  page_url?: string;
  schemas?: Array<Record<string, unknown>>;
  items?: Array<Record<string, unknown>>;
}

interface BeSchemaResponse {
  types?: Array<Record<string, unknown>>;
  install?: Record<string, unknown>;
  by_url?: BeSchemaByUrl[];
  pages?: BeSchemaByUrl[];
  validation?: Record<string, unknown>;
}

const SCHEMA_NAME_OVERRIDES: Record<string, string> = {
  localbusiness: 'LocalBusiness',
  dentist: 'Dentist',
  faqpage: 'FAQPage',
  webpage: 'WebPage',
  aggregaterating: 'AggregateRating',
  breadcrumblist: 'BreadcrumbList',
  medicalcondition: 'MedicalCondition',
};

function formatSchemaName(name: string): string {
  const key = name.toLowerCase().replace(/[\s_-]/g, '');
  if (SCHEMA_NAME_OVERRIDES[key]) return SCHEMA_NAME_OVERRIDES[key];
  return name;
}

function formatWhen(isoOrLabel: string): string {
  if (!isoOrLabel || isoOrLabel === '—') return '—';
  if (isoOrLabel.includes('T') || isoOrLabel.endsWith('Z')) {
    return relativeTimeFromIso(isoOrLabel);
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoOrLabel)) {
    const parsed = Date.parse(`${isoOrLabel}T12:00:00Z`);
    if (Number.isFinite(parsed)) return relativeTimeFromIso(new Date(parsed).toISOString());
  }
  return isoOrLabel;
}

function adaptInstall(raw: Record<string, unknown> | undefined): ScInstallInfo {
  if (!raw) {
    return { status: 'pending', pagesDetected: 0 };
  }
  const c = deepCamel<Record<string, unknown>>(raw);
  const status = enumLower(str(c.status, 'pending'), 'pending' as string);
  const install: ScInstallInfo = {
    status: status === 'installed' ? 'installed' : 'pending',
    pagesDetected: num(c.pagesDetected),
  };
  const installedDate = str(c.installedDate, '');
  const lastPing = str(c.lastPing, '');
  if (installedDate) install.installedDate = formatWhen(installedDate);
  if (lastPing) install.lastPing = formatWhen(lastPing);
  return install;
}

function mapSchemaStatus(wire: string): ScStatusKey {
  const status = enumLower(wire, 'missing' as string);
  if (['active', 'deployed', 'live', 'installed', 'marked_deployed'].includes(status)) {
    return 'active';
  }
  if (['warning', 'partial', 'incomplete'].includes(status)) return 'warning';
  if (['error', 'failed', 'invalid'].includes(status)) return 'error';
  if (['missing', 'inactive', 'not_deployed', 'pending'].includes(status)) return 'missing';
  return 'missing';
}

function mapSchemaPriority(wire: string): ScPriorityKey {
  const priority = enumLower(wire, 'recommended' as string);
  if (priority === 'essential' || priority === 'required') return 'essential';
  if (priority === 'important') return 'important';
  if (priority === 'nice_to_have' || priority === 'optional') return 'nice_to_have';
  return 'recommended';
}

function resolvePageId(c: Record<string, unknown>, row: Record<string, unknown>): number | undefined {
  const pageIdsRaw = c.pageIds ?? c.page_ids ?? row.page_ids;
  if (Array.isArray(pageIdsRaw)) {
    for (const entry of pageIdsRaw) {
      if (typeof entry === 'number' && Number.isFinite(entry) && entry > 0) {
        return entry;
      }
      const asStr = str(entry, '').trim();
      if (/^\d+$/.test(asStr)) return num(asStr);
    }
  }

  const candidates = [c.pageId, c.page_id, row.page_id, c.schemaPageId, c.schema_page_id];
  for (const candidate of candidates) {
    if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0) {
      return candidate;
    }
    const asStr = str(candidate, '').trim();
    if (/^\d+$/.test(asStr)) return num(asStr);
  }
  return undefined;
}

function resolveTypeSlug(c: Record<string, unknown>, typeName: string): string {
  const rawId = str(c.id, '').trim();
  if (rawId && !/^\d+$/.test(rawId)) return rawId;
  return typeName.toLowerCase().replace(/\s+/g, '');
}

function adaptSchemaType(row: Record<string, unknown>): ScSchemaItem {
  const c = deepCamel<Record<string, unknown>>(row);
  const typeName = formatSchemaName(str(c.name ?? c.type ?? c.schemaType ?? c.id, 'Schema'));
  const description = str(c.description, '').trim();
  const pageId = resolvePageId(c, row);

  const item: ScSchemaItem = {
    id: resolveTypeSlug(c, typeName),
    name: typeName,
    status: mapSchemaStatus(str(c.status, 'missing')),
    priority: mapSchemaPriority(str(c.priority, 'recommended')),
    description: description || '—',
    deployedOn: str(c.deployedOn ?? c.deployed_on, '—'),
    source: str(c.source, 'ELVA'),
    ...(c.lastUpdated || c.last_updated
      ? { lastUpdated: formatWhen(str(c.lastUpdated ?? c.last_updated)) }
      : {}),
    ...(c.issue ? { issue: str(c.issue) } : {}),
  };
  if (pageId != null) item.pageId = pageId;
  return item;
}

/** Backend may return per-type (`types[]`) or per-URL groups — normalize to per-type items. */
export function adaptSchemaItems(be: BeSchemaResponse): ScSchemaItem[] {
  if (be.types?.length) {
    return be.types.map(adaptSchemaType);
  }

  const groups = be.by_url ?? be.pages ?? [];
  const items: ScSchemaItem[] = [];

  for (const group of groups) {
    const url = str(group.url ?? group.page_url, '—');
    const schemas = group.schemas ?? group.items ?? [];
    for (const raw of schemas) {
      const c = deepCamel<Record<string, unknown>>(raw);
      const typeName = formatSchemaName(str(c.name ?? c.type ?? c.schemaType, 'Schema'));
      const pageId = resolvePageId(c, raw);
      const item: ScSchemaItem = {
        id: resolveTypeSlug(c, typeName),
        name: typeName,
        status: mapSchemaStatus(str(c.status, 'missing')),
        priority: mapSchemaPriority(str(c.priority, 'recommended')),
        description: str(c.description, '—') || '—',
        deployedOn: str(c.deployedOn ?? url),
        source: str(c.source, 'ELVA'),
        ...(c.lastUpdated ? { lastUpdated: formatWhen(str(c.lastUpdated)) } : {}),
        ...(c.issue ? { issue: str(c.issue) } : {}),
      };
      if (pageId != null) item.pageId = pageId;
      items.push(item);
    }
  }

  return items;
}

function adaptValidation(raw: Record<string, unknown> | undefined): ScValidation {
  if (!raw) {
    return {
      valid: 0,
      warnings: 0,
      errors: 0,
      lastValidated: '—',
      issues: [],
    };
  }
  const c = deepCamel<Record<string, unknown>>(raw);
  const lastRaw = str(c.lastValidated ?? c.last_validated, '—');
  return {
    valid: num(c.valid),
    warnings: num(c.warnings),
    errors: num(c.errors),
    lastValidated: lastRaw === '—' ? '—' : formatWhen(lastRaw),
    issues: ((c.issues as Array<Record<string, unknown>> | undefined) ?? []).map((row) => {
      const issue = deepCamel<Record<string, unknown>>(row);
      return {
        schema: formatSchemaName(str(issue.schema)),
        message: str(issue.message),
        severity: (enumLower(issue.severity as string, 'warning' as string) === 'error'
          ? 'error'
          : 'warning') as 'warning' | 'error',
      };
    }),
  };
}

const METRIC_TONE: Record<string, ScToneKey> = {
  types_active: 'ai',
  essential_coverage: 'success',
  pages_covered: 'info',
  validation: 'success',
};

function adaptMetricTone(key: string, tone?: string): ScToneKey | undefined {
  const normalized = str(tone, '').toLowerCase();
  if (normalized === 'success') return 'success';
  if (normalized === 'gold' || normalized === 'warning') return 'gold';
  if (normalized === 'terra' || normalized === 'error') return 'terra';
  if (normalized === 'info') return 'info';
  if (normalized === 'ai') return 'ai';
  return METRIC_TONE[key];
}

function buildFallbackMetrics(
  items: ScSchemaItem[],
  validation: ScValidation,
  install: ScInstallInfo,
): ScMetricTile[] {
  const activeCount = items.filter((item) => item.status === 'active').length;
  const essentialTotal = items.filter((item) => item.priority === 'essential').length;
  const essentialDone = items.filter(
    (item) => item.priority === 'essential' && item.status === 'active',
  ).length;
  const validationValue =
    validation.errors === 0 && validation.warnings === 0
      ? 'Clean'
      : validation.errors > 0
        ? `${validation.errors} errors`
        : `${validation.warnings} warning${validation.warnings === 1 ? '' : 's'}`;

  return [
    {
      key: 'types_active',
      label: 'Schema types active',
      value: String(activeCount),
      total: `/ ${items.length}`,
      sub: 'marked deployed (self-attested)',
      tone: 'ai',
    },
    {
      key: 'essential_coverage',
      label: 'Essential coverage',
      value: `${essentialDone} / ${essentialTotal}`,
      sub: 'essential schemas marked deployed',
      tone: essentialDone === essentialTotal ? 'success' : 'gold',
    },
    {
      key: 'pages_covered',
      label: 'Pages covered',
      value: String(install.pagesDetected),
      sub: 'via injector script',
      tone: 'info',
    },
    {
      key: 'validation',
      label: 'Validation buckets',
      value: validationValue,
      sub: 'ELVA bucket counts',
      tone: validation.errors > 0 ? 'terra' : validation.warnings > 0 ? 'gold' : 'success',
    },
  ];
}

function adaptMetrics(
  raw: Array<Record<string, unknown>> | undefined,
  fallback: ScMetricTile[],
): ScMetricTile[] {
  if (!raw?.length) return fallback;

  return raw.map((row) => {
    const metric = deepCamel<Record<string, unknown>>(row);
    const key = str(metric.key, 'metric');
    const value = str(metric.value);
    const parts = value.includes('/') ? value.split('/').map((part) => part.trim()) : undefined;
    const tone = adaptMetricTone(key, metric.tone != null ? str(metric.tone) : undefined);
    const tile: ScMetricTile = {
      key,
      label: str(metric.label),
      value: parts?.[0] ?? value,
      sub:
        key === 'validation'
          ? 'ELVA bucket counts'
          : key === 'essential_coverage'
            ? 'essential schemas marked deployed'
            : key === 'pages_covered'
              ? 'via injector script'
              : 'across all pages',
    };
    if (parts?.[1]) tile.total = `/ ${parts[1]}`;
    if (tone) tile.tone = tone;
    return tile;
  });
}

export interface SchemaData {
  items: ScSchemaItem[];
  install: ScInstallInfo;
  validation: ScValidation;
  metrics: ScMetricTile[];
}

export async function fetchSchema(): Promise<SchemaData> {
  const [be, summary] = await Promise.all([
    growthGet<BeSchemaResponse>('/seo/schema/').catch(() => ({}) as BeSchemaResponse),
    growthGet<{ metrics?: Array<Record<string, unknown>> }>('/seo/schema/summary/').catch(
      () => undefined,
    ),
  ]);

  const items = adaptSchemaItems(be);
  const install = adaptInstall(be.install);
  const validation = adaptValidation(be.validation);
  const fallbackMetrics = buildFallbackMetrics(items, validation, install);
  const metrics = adaptMetrics(summary?.metrics, fallbackMetrics);

  return { items, install, validation, metrics };
}

export async function deploySchema(pageId: number) {
  const { growthPostAsync } = await import('@/data/http');
  return growthPostAsync(`/seo/schema/${pageId}/handoff/`);
}

export async function markSchemaDeployed(pageId: number) {
  const { growthPost } = await import('@/data/http');
  return growthPost(`/seo/schema/${pageId}/mark-deployed/`);
}
